// Fiche d'une séance / d'un match (une occurrence) :
//  - un match (D-058) : en-tête selon le moment, onglets Avant / Direct / Après, « À faire » du coach ;
//  - ma réponse (et celle de mes enfants) : attendue par défaut, absente ou incertaine en un geste (D-116) ; une
//    absence d'office par une règle de la semaine se lève pour cette séance seulement (D-114) ;
//  - pour le staff : la feuille de présences en trois groupes (incertaines, absentes, attendues), renforts et
//    indisponibilités compris, « sans échauffement » au match, l'exception « La remettre présente » ;
//  - D-148 : « prévenu tard » et « sans prévenir » sur la feuille du staff (la raison, le mot, « Ne pas compter », « Ne pas
//    lui écrire ») ; après la limite, « Je serai absente » ouvre la feuille du désistement tardif.
import './indispos.css';
import {
  cancelOccurrence, eventById, inviteRenfort, matchesFor, occurrenceException, sessionPlan, teamMatches, teamRoster, occurrenceRoster, removeRenfort, renfortCandidates, renfortWeekend, responsesFor, saveMatch, setResponse,
  type Convoked, type RenfortCandidate, type RenfortGame, type TeamMatch,
} from '../lib/api';
import { localDate, occurrences } from '../domain/schedule';
import { renderDayCards, renderMyDay, sameDayMatches } from './match-day';
import { expectedOf, presenceRate, resolveAttendance, verdictOf } from '../domain/attendance';
import { clock, dayName, hm, isoDow, ruleSentence } from '../domain/availability';
import { crowKm, gameWhere, hm as hmMin, renfortDouble, roadEstimate, type GameLite } from '../domain/match-day';
import { renfortHint, renfortNotOffered, verifyLabel, verifyText } from '../domain/renforts';
import { attCtx, inlineNote, leadsFor, loadAvail } from './availability-data';
import { periodRange } from './unavailability';
import { agree, displayName, playerNoun } from '../domain/labels';
import { genderOf, hatShowing, isMinorAccount, modOn, squadName, staffHatFor, switchHat, teamOf, type Ctx } from './context';
import { absenceExample, isMinorPerson } from '../domain/minors';
import { KIND_LABEL, longDate, shortDate, time, timeRange } from './format';
import { $, $$, confirmTwice, html, mount, openSheet, toast, type Html } from './dom';
import { COMPETITION_LABEL } from '../domain/ffbb';
import { outcome } from '../domain/results';
import { scoreLine } from './team-matches';
import { renderPlanBlock } from './plan';
import { renderMatchBlocks } from './match-blocks';
import { renderStatsBlock } from './live-stats';
import { openEventEdit } from './event-form';
import { openLive } from './live';
import { totalMinutes } from '../domain/session';
import { renderPresentedBy } from './presented';
import { renderMatchLien } from './lien';
import { renderMatchGuests } from './lien-mail';
import { renderMatchSupport } from './supporters';
import { renderMatchAlert, renderMatchDocs, renderMatchPhotos } from './match-extras';
import { renderMatchVolunteers } from './besoins';
import { renderTrip } from './trip';
import { alertMine, ffbbAlertsMine, ffbbLinksOf, matchActions, matchLineup, matchPrepForMe, matchTrip } from '../lib/api';
import { matchLabel } from '../domain/match-label';
import { boxScore, byQuarter, score, type Action } from '../domain/boxscore';
import { countdown, phaseOf, type Phase } from '../domain/match-phase';
import { parseLocalDate } from '../domain/schedule';
import { heroChip, matchHero, phaseTabs, renderMatchTodo, renderResultShare, type HeroInput } from './match-page';
import { crestLogo, fillCrests, oppCrest } from './crest';
import { renderFetchScore, renderScoreAlert } from './score-sync';
import { canFetchScore, resultConfirmed } from '../domain/score';
import { seanceSeg } from './seance';
import { renderFeelingEventBlock } from './ressentis';
import { boardSub, boardTop, boardWhy, openLateSheet, wireBoard } from './desist';
import { absenceBoard, absenceDeadline, type BoardNotice, type Deadline } from '../lib/desist-api';

/** L'onglet choisi sur chaque match, le temps de la visite. */
const tabOf = new Map<string, Phase>();
/** Les groupes de la feuille de présences (D-116), figés le temps de la visite : une ligne corrigée reste à sa place (D-092). */
const frozenGroups = new Map<string, Map<string, 'u' | 'a' | 'p'>>();
const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);

/** `query` : la réponse d'une relance (« ?confirmer=<personne>&r=present|absent », notification D-116). */
export async function renderEvent(root: HTMLElement, ctx: Ctx, eventId: string, date: string, query?: URLSearchParams): Promise<void> {
  const ev = await eventById(eventId);
  const occ = ev ? occurrences(ev, date, date)[0] : undefined;
  if (!ev || !occ) {
    mount(root, html`<section class="screen"><a class="back" href="#/" data-back>‹ Retour</a>
      <div class="empty"><p class="empty-title">Ce rendez-vous n'existe pas (ou plus).</p></div></section>`);
    return;
  }
  // Casquettes (D-072) : une séance d'une équipe d'une autre casquette s'ouvre dans cette casquette.
  const otherHat = hatShowing(ctx, ev.team_id);
  if (otherHat) { switchHat(ctx, otherHat); window.dispatchEvent(new HashChangeEvent('hashchange')); return; }
  // D-117 : une alerte FFBB qui m'est adressée s'ouvre dans la casquette où je tranche (coach, bureau…), même depuis la
  // casquette Parent ou Joueuse de la même équipe (cloche, lien partagé).
  const decideHat = ev.kind === 'match' ? staffHatFor(ctx, ev.team_id) : null;
  if (decideHat && (await ffbbAlertsMine().catch(() => [])).some(a => a.event_id === eventId)) {
    switchHat(ctx, decideHat); window.dispatchEvent(new HashChangeEvent('hashchange')); return;
  }
  const team = teamOf(ctx, ev.team_id);
  const staff = ctx.staffOf.has(ev.team_id);
  // La réponse donnée depuis la relance (deux boutons sur la notification) : enregistrée, puis la fiche s'ouvre.
  const confirmFor = query?.get('confirmer'), answer = query?.get('r');
  // D-148 : « Je serai absente » depuis la relance, après la limite : la feuille du désistement tardif, une fois la fiche ouverte.
  let lateAsk: Deadline | null = null;
  if (confirmFor && answer === 'absent' && ctx.people.some(p => p.id === confirmFor)) {
    const dl = await absenceDeadline(eventId, date, confirmFor);
    if (dl?.late) lateAsk = dl;
  }
  if (confirmFor && (answer === 'present' || answer === 'absent') && !lateAsk && ctx.people.some(p => p.id === confirmFor)) {
    try {
      await setResponse(eventId, date, confirmFor, answer, null);
      const g0 = genderOf(ctx, confirmFor), who = ctx.people.find(p => p.id === confirmFor)!, self = who.user_id === ctx.uid;
      toast(answer === 'present' ? `C’est noté : ${self ? 'tu es' : `${displayName(who)} est`} ${agree(g0, 'attendu', 'attendue')}`
        : `C’est noté : ${self ? 'ton absence' : `l’absence de ${displayName(who)}`}, le coach est prévenu`);
    } catch { toast('Enregistrement impossible. Réessaie.', 'ko'); }
  }
  if (query && [...query.keys()].length) history.replaceState(history.state, '', `#/e/${eventId}/${date}`);
  const [roster, responses, avail, leads, matchRows, groupMatches, board] = await Promise.all([
    occurrenceRoster(eventId, date), responsesFor(eventId, date), loadAvail(), leadsFor(teamOf(ctx, ev.team_id)?.club_id ?? ctx.club?.id),
    ev.kind === 'match' ? matchesFor([eventId]) : Promise.resolve([]),
    ev.kind === 'match' && team ? teamMatches(ev.team_id).catch(() => [] as TeamMatch[]) : Promise.resolve([] as TeamMatch[]),
    staff && (ev.kind === 'training' || ev.kind === 'match') ? absenceBoard(eventId, date) : Promise.resolve([] as BoardNotice[]),   // D-148
  ]);
  const notices = new Map(board.map(n => [n.person_id, n]));
  const match = matchRows[0] ?? null;
  // Jour de double match (D-092) : l'autre équipe engagée du groupe joue aussi ce jour-là.
  const dayOthers = match ? sameDayMatches(groupMatches, eventId, match.squad_id, date) : [];
  const meMatch = groupMatches.find(m => m.event_id === eventId);
  // D-113 : un 0-0 venu de la FFBB n'est pas un résultat (sûrement une erreur) : ni « Match nul », ni carte à partager.
  const res = match && resultConfirmed(match) ? outcome(match) : null;
  const [actions, prepMe, lineup, oppLogo] = match ? await Promise.all([
    matchActions(eventId).catch(() => [] as Action[]),
    staff ? Promise.resolve(null) : matchPrepForMe(eventId).catch(() => null),
    staff ? Promise.resolve([]) : matchLineup(eventId).catch(() => []),
    crestLogo(match),   // le logo du club adverse (annuaire des clubs, D-094)
  ]) : [[] as Action[], null, [], null];
  const gender = team?.gender ?? (ctx.people[0] ? genderOf(ctx, ctx.people[0].id) : 'X');
  const nameOf = (c: Convoked) => displayName(c);

  // LA règle de l'occurrence (D-114) : périodes, règles de la semaine, l'heure, la route et le rendez-vous d'un match.
  const actx = attCtx(avail, ev, occ, match, leads);
  const members = roster.filter(r => !r.invited).map(r => r.person_id);
  const invited = roster.filter(r => r.invited).map(r => r.person_id);
  const att = resolveAttendance(members, invited, responses, actx);
  const weekday = dayName(isoDow(date));
  const mine = roster.filter(r => ctx.people.some(p => p.id === r.person_id));

  // Un parent répond pour son enfant (D-103) : le prénom de l'enfant, jamais « Ma présence · Tu es attendue ».
  const forChild = (pid: string) => ctx.people.some(p => p.id === pid && p.user_id !== ctx.uid);
  const myBlock = mine.map(c => {
    const g = genderOf(ctx, c.person_id) || gender;
    const r = responses.find(x => x.personId === c.person_id);
    const v = verdictOf(c.person_id, actx);
    const st: 'present' | 'absent' | 'uncertain' = r ? r.status : v?.state === 'off' ? 'absent' : 'present';
    const kid = forChild(c.person_id);
    const named = mine.length > 1 || ctx.people.length > 1 || kid;
    const subj = named ? `${nameOf(c)} est` : 'Tu es';
    const their = kid ? 'sa' : 'ta';
    // Trois réponses (D-116), accordées au genre ; pour un enfant, son prénom est au-dessus.
    const L = kid ? { present: agree(g, 'Attendu', 'Attendue'), absent: agree(g, 'Absent', 'Absente'), uncertain: agree(g, 'Incertain', 'Incertaine') }
      : { present: `Je suis ${agree(g, 'attendu', 'attendue')}`, absent: `Je serai ${agree(g, 'absent', 'absente')}`, uncertain: agree(g, 'Incertain', 'Incertaine') };
    const period = v?.source === 'period' ? avail.periodRows.find(u => u.id === v.ref) : null;
    const once = ev.kind === 'match' ? 'ce match' : 'cette séance';
    let note: Html | '' = '';
    if (!r && !v) note = html`<p class="reply-note">${subj} ${agree(g, 'attendu', 'attendue')} par défaut : change seulement en cas d'absence.</p>`;
    else if (!r && v?.state === 'late') note = html`<p class="reply-note">${v.freeAt ? `Libre à ${hm(v.freeAt)} (${their} règle du ${weekday})` : `${cap(their)} règle du ${weekday}`} : arrivée après le rendez-vous${v.meetMin !== null ? ` (${clock(v.meetMin)})` : ''}, le coach ${kid ? agree(g, 'le', 'la') : 'te'} voit « sans échauffement ». ${subj} ${agree(g, 'attendu', 'attendue')} pour le match.</p>`;
    else if (!r && v?.state === 'off' && v.source === 'rule') note = html`<p class="reply-note"><span class="ix-out">${subj} ${agree(g, 'indiqué absent', 'indiquée absente')} (${their} règle du ${weekday})</span>. Pour ${once} seulement : « ${L.present} » ou « ${L.uncertain} » ; la règle reste.</p>`;
    else if (!r && v?.state === 'off') note = html`<p class="reply-note">Indisponible${period ? ` du ${periodRange(period.starts_on, period.ends_on)}` : ''} : absence notée d'office. ${kid ? `${nameOf(c)} peut` : 'Tu peux'} quand même répondre « ${L.present} ».</p>`;
    else if (r && v?.state === 'off' && r.status !== 'absent') note = html`<p class="reply-note">Pour ${once} seulement : ${v.source === 'rule' ? `${their} règle du ${weekday} reste pour les autres semaines` : `${their} indisponibilité reste pour les autres jours`}.</p>`;
    const relance = ev.kind === 'match' ? 'la veille du match' : 'ce matin-là';
    const field = st === 'absent' && r ? html`
          <label class="field field-inline">
            <span>Motif (facultatif)</span>
            <input data-reason data-st="absent" maxlength="120" value="${r.reason ?? ''}" placeholder="${absenceExample(isMinorPerson(ctx.people.find(p => p.id === c.person_id)))}">
          </label>`
      : st === 'uncertain' ? html`
          <label class="field ix-note-f"><span>Un mot pour le coach (facultatif)</span>
            <input data-reason data-st="uncertain" maxlength="80" value="${r?.reason ?? ''}" placeholder="ex. réunion qui peut finir tard"></label>
          <p class="reply-note">${actx.past ? `La séance est passée : sans correction du coach, ${kid ? `${nameOf(c)} compte` : 'tu comptes'} comme ${agree(g, 'présent', 'présente')}.`
            : `Le coach ${kid ? agree(g, 'le', 'la') : 'te'} compte comme ${agree(g, 'incertain', 'incertaine')}. Pas de détail médical : pour une blessure, « ${L.absent} ». Sans réponse, une relance ${relance}.`}</p>` : '';
    return html`
      <div class="reply" data-pid="${c.person_id}">
        ${mine.length > 1 || ctx.people.length > 1 ? html`<div class="reply-who">${nameOf(c)}</div>` : ''}
        <div class="seg seg-reply ix-seg3" role="group" aria-label="${kid ? `Réponse pour ${nameOf(c)}` : 'Ma réponse'}" data-guide="ev-reply">
          <button type="button" data-set="present" aria-pressed="${String(st === 'present')}">${L.present}</button>
          <button type="button" data-set="absent" aria-pressed="${String(st === 'absent')}">${L.absent}</button>
          <button type="button" data-set="uncertain" aria-pressed="${String(st === 'uncertain')}">${L.uncertain}</button>
        </div>
        ${field}
        ${note}
      </div>`;
  });

  const presenceTitle = mine.length > 1 ? 'Présences' : mine[0] && forChild(mine[0].person_id) ? `Présence de ${nameOf(mine[0])}` : 'Ma présence';

  // Feuille du staff (match et appel des entraînements) : trois choix par ligne (présente, incertaine « ? »,
  // absente), celui en cours rempli ; trois groupes (D-116), figés le temps de la visite : une ligne corrigée
  // reste à sa place (D-092). Les absentes d'office sont nommées en tête, avec leur raison (D-114).
  const absentOf = new Map(att.absent.map(a => [a.personId, a]));
  const uncertainOf = new Map(att.uncertain.map(u => [u.personId, u]));
  const P = agree(gender, 'Présent', 'Présente'), A = agree(gender, 'Absent', 'Absente'), I = agree(gender, 'Incertain', 'Incertaine');
  const liveGroup = (pid: string): 'u' | 'a' | 'p' => (uncertainOf.has(pid) ? 'u' : absentOf.has(pid) ? 'a' : 'p');
  const fkey = `${eventId}:${date}`;
  let fz = frozenGroups.get(fkey);
  if (!fz && staff) {
    fz = new Map(roster.map(r => [r.person_id, liveGroup(r.person_id)]));
    frozenGroups.set(fkey, fz);
    window.addEventListener('hashchange', () => frozenGroups.delete(fkey), { once: true });
  }
  const groupOf = (pid: string) => fz?.get(pid) ?? liveGroup(pid);
  const marked = (pid: string) => att.late.has(pid) || att.overridden.has(pid) || att.unconfirmed.includes(pid);
  const rowFor = (c: Convoked) => {
    const pid = c.person_id;
    const st = liveGroup(pid);
    const off = absentOf.get(pid), unc = uncertainOf.get(pid), late = att.late.get(pid), ov = att.overridden.get(pid);
    let why: Html | '' = '';
    if (unc) why = html`${I}${unc.reason ? ` · « ${inlineNote(unc.reason)} »` : ''}${ov ? ` · ${ov.label ?? 'indispo'}` : ''}`;
    else if (att.unconfirmed.includes(pid)) why = html`${I}, pas ${agree(gender, 'confirmé', 'confirmée')} : ${agree(gender, 'compté présent', 'comptée présente')}`;
    else if (off?.source === 'rule') why = html`${A} (${off.label ?? weekday})`;
    else if (off?.source === 'unavailability') why = html`Indispo${off.medical ? html` <span class="tag-med">médical</span>` : ''}`;
    else if (off) why = off.reason ? html`${A} · « ${inlineNote(off.reason)} »${off.medical ? html` <span class="tag-med">médical</span>` : ''}` : off.medical ? html`${A} <span class="tag-med">médical</span>` : '';
    else if (late) why = html`${late.freeAt ? `Libre à ${hm(late.freeAt)} · ` : ''}<span class="ix-warm">sans échauffement</span>`;
    else if (ov) why = html`Vient quand même (${ov.label ?? 'indispo'})`;
    // D-148 : prévenu tard, sans prévenir (la raison, le mot, « Ne pas compter », « Ne pas lui écrire »).
    const nt = st === 'a' ? notices.get(pid) : undefined;
    if (nt) why = boardWhy(nt);
    const back = staff && off?.source === 'rule'
      ? html`<button type="button" class="ix-back" data-unrule="${pid}">↺ ${agree(gender, 'Le remettre présent', 'La remettre présente')} pour ${ev.kind === 'match' ? 'ce match' : 'cette séance'}</button>` : '';
    const hl = !!why && (st !== 'p' || marked(pid));
    return html`
      <li class="pl-row pl-st ${nt ? (nt.kind === 'late' ? 'ds-hl' : 'ds-hlk') : hl ? 'ix-hl' : ''}" data-pid="${pid}">
        <span class="num">${c.jersey_number ?? ''}</span>
        <span class="pl-name">${nameOf(c)}${c.invited ? html` <span class="kind kind-renfort">Renfort</span>` : ''}${staff && c.invited
          ? html` <button type="button" class="link-x" data-unrenfort="${pid}" aria-label="Retirer la convocation de ${nameOf(c)}">retirer</button>` : ''}</span>
        <span class="st2" role="group" aria-label="Présence de ${nameOf(c)}">
          <button type="button" class="${st === 'p' ? 'on ok' : ''}" data-pres="${pid}" data-v="present" aria-pressed="${String(st === 'p')}">${st === 'p' ? '✓ ' : ''}${P}</button>
          <button type="button" class="ix-q ${st === 'u' ? 'on' : ''}" data-pres="${pid}" data-v="uncertain" aria-pressed="${String(st === 'u')}" aria-label="${I}">?</button>
          <button type="button" class="${st === 'a' ? 'on ko' : ''}" data-pres="${pid}" data-v="absent" aria-pressed="${String(st === 'a')}">${st === 'a' ? '✕ ' : ''}${A}</button>
        </span>
        ${why ? html`<span class="pl-why ix-why ix-whyrow">${why}</span>` : ''}
        ${nt ? html`<span class="ds-sub">${boardSub(nt, gender)}</span>` : ''}
        ${back}
      </li>`;
  };
  const GROUPS: { g: 'u' | 'a' | 'p'; label: string }[] = [
    { g: 'u', label: agree(gender, 'Incertains', 'Incertaines') },
    { g: 'a', label: agree(gender, 'Absents', 'Absentes') },
    { g: 'p', label: actx.past ? agree(gender, 'Présents', 'Présentes') : agree(gender, 'Attendus', 'Attendues') },
  ];
  const grouped = GROUPS.map(x => {
    const list = roster.filter(r => groupOf(r.person_id) === x.g);
    if (x.g === 'p') list.sort((a, b) => Number(marked(b.person_id)) - Number(marked(a.person_id)));
    return list.length ? html`<li class="ix-gh ix-gh-${x.g}">${x.label} · ${list.length}</li>${list.map(rowFor)}` : '';
  });
  // « D'office » : qui l'est, et pourquoi (jamais la nature d'un problème médical, D-009).
  const nm = (pid: string) => displayName(roster.find(r => r.person_id === pid) ?? { first_name: '—', nickname: null });
  const offItems = att.absent.filter(a => a.source !== 'response').map(a => {
    if (a.source === 'unavailability') return html`<b>${nm(a.personId)}</b> (indispo${a.medical ? ', médical' : ''})`;
    const v = verdictOf(a.personId, actx);
    if (ev.kind === 'match' && v?.freeAt) return html`<b>${nm(a.personId)}</b>, libre à ${hm(v.freeAt)}${v.travelMin ? ` avec ${hmMin(v.travelMin)} de route` : ''}, ne peut pas arriver pour ${time(occ.start)}`;
    return html`<b>${nm(a.personId)}</b> (${a.label ?? weekday})`;
  });
  const lates = [...att.late];
  const offBox = offItems.length ? html`<p class="ix-off">D’office : ${offItems.map((x, i) => html`${i ? ' · ' : ''}${x}`)}</p>` : '';
  const lateBox = lates.length ? html`<p class="ix-off">Échauffement à ${clock(lates[0]![1].meetMin ?? 0)}. ${lates.map(([pid, v]) => html`<b>${nm(pid)}</b>, ${v.freeAt ? `libre à ${hm(v.freeAt)} le ${weekday}${v.travelMin ? ` (${hmMin(v.travelMin)} de route)` : ''}, ` : ''}arrive pour le début du match. `)}</p>` : '';
  const total = att.present.length + att.uncertain.length + att.absent.length;
  const sheetBlock = staff ? html`
    <section class="block" id="ev-sheet" data-guide="ev-presences">
      <div class="block-head">
        <h3>Présences</h3>
        <span class="gauge" style="--p:${presenceRate(att)}">${att.present.length} / ${total}</span>
      </div>
      <h4 class="sub">${att.present.length} ${actx.past ? agree(gender, 'présent', 'présente', att.present.length) : agree(gender, 'attendu', 'attendue', att.present.length)}${att.uncertain.length ? html` · <span class="ix-unc">${att.uncertain.length} ${agree(gender, 'incertain', 'incertaine', att.uncertain.length)}</span>` : ''}${att.absent.length ? ` · ${att.absent.length} ${agree(gender, 'absent', 'absente', att.absent.length)}` : ''}</h4>
      ${staff ? boardTop(board.filter(n => absentOf.has(n.person_id))) : ''}${offBox}${lateBox}
      <ul class="pl-list">${grouped}</ul>
      ${roster.length === 0 ? html`<p class="muted">Aucun${gender === 'F' ? 'e' : ''} ${playerNoun(gender)} dans l'effectif.</p>` : ''}
      <button type="button" class="btn btn-ghost btn-sm" id="b-renfort" data-guide="ev-renfort">+ Convoquer un renfort</button>
    </section>` : '';
  const editButtons = staff ? html`
    <button type="button" class="btn btn-ghost" id="b-edit-ev">${ev.recurrence?.weekdays?.length ? 'Modifier la série (jours, heures, lieu)' : 'Modifier'}</button>
    <button type="button" class="btn btn-danger-ghost" id="b-cancel" data-guide="ev-cancel">${ev.recurrence?.weekdays?.length ? 'Annuler cette séance (les autres restent)' : 'Annuler'}</button>` : '';
  const sheet = staff ? html`${sheetBlock}${editButtons}` : '';
  // D-122 : « Match présenté par » avec le module Partenaires, les bénévoles du match avec le Bénévolat.
  const partnersOn = !!team && modOn(ctx, 'partenaires', team.club_id);
  const volunteersOn = !!team && modOn(ctx, 'benevolat', team.club_id);

  // D-113 : sur un match FFBB aussi, une fois commencé ; le score saisi au club reste, la FFBB ne l'écrase plus.
  const scoreForm = staff && (!match?.ffbb_match_id || occ.start.getTime() <= Date.now()) ? html`
    <section class="block" id="ev-score-form">
      <h3>${res ? 'Score' : 'Saisir le score'}</h3>
      ${match?.ffbb_match_id ? html`<p class="hint">${match.score_source === 'club' ? 'Saisi au club : il reste, même si la FFBB affiche autre chose ; une alerte te prévient en cas d’écart.'
        : match.score_source === 'ffbb' ? 'Score de la FFBB. Corrige-le ici si besoin : le score saisi au club reste.'
        : 'Saisi ici, il reste, même si la FFBB affiche autre chose ; une alerte te prévient en cas d’écart.'}</p>` : ''}
      <form class="score-form" id="f-score" novalidate>
        <label><span class="muted small">${squadName(ctx, match?.squad_id) ?? team?.name ?? 'Nous'}</span><input name="us" type="number" inputmode="numeric" min="0" max="300" value="${match?.score_us ?? ''}"></label>
        <span class="score-sep">–</span>
        <label><span class="muted small">${match?.opponent ?? ev.title}</span><input name="them" type="number" inputmode="numeric" min="0" max="300" value="${match?.score_them ?? ''}"></label>
        <button class="btn btn-primary btn-sm" type="submit">Enregistrer</button>
      </form>
    </section>` : '';
  const teamLabel = squadName(ctx, match?.squad_id) ?? team?.name ?? 'Nous';
  const phase: Phase = match ? (tabOf.get(eventId) ?? phaseOf(occ.start, match)) : 'avant';
  const sc = score(actions);
  const hero: HeroInput | null = match ? {
    phase, staff, club: ctx.clubs.find(c => c.id === team?.club_id) ?? ctx.club, team: teamLabel, opponent: match.opponent, isHome: match.is_home,
    competition: COMPETITION_LABEL[match.competition], start: occ.start, time: time(occ.start), location: ev.location,
    dayLabel: parseLocalDate(date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }),
    live: match.live ? { us: sc.us, them: sc.them, quarter: match.live_quarter ?? 1, quarters: byQuarter(actions) } : null,
    result: res ? { kind: res, text: scoreLine(match) } : null,
    countdown: countdown(date, localDate(new Date())),
    retained: !staff && prepMe?.lineup_announced && lineup.length ? mine.some(c => lineup.some(l => l.person_id === c.person_id && l.selected)) : null,
    gender: team?.gender ?? 'X',
    opponentLogo: oppLogo,
  } : null;
  const pane = (p: Phase) => (p === phase ? '' : 'hidden');

  if (match && team && hero) mount(root, html`
    <section class="screen mp">
      <div class="pp-nav"><a class="back" href="#/matchs" data-back>‹ Retour</a>${heroChip(hero)}</div>
      ${matchHero(hero)}
      <div id="ev-fetch"></div>
      ${staff ? html`<div id="ev-score"></div><div id="ev-alert"></div>` : ''}
      ${staff && dayOthers.length ? html`<div id="ev-day" class="screen"></div>` : ''}
      ${phaseTabs(phase)}
      <div class="screen mp-pane" data-pane="avant" ${pane('avant')}>
        ${staff && !res ? html`<div id="ev-todo"></div>` : ''}
        ${sheetBlock}
        ${!staff && dayOthers.length ? html`<div id="ev-my-day"></div>` : ''}
        ${mine.length ? html`<section class="block"><h3>${presenceTitle}</h3>${myBlock}</section>` : ''}
        ${match.is_home === false ? html`<div id="ev-trip"></div>` : ''}
        <div id="ev-match" class="screen"></div>
        ${staff && match.is_home && volunteersOn ? html`<div id="ev-vol"></div>` : ''}
        ${ev.note ? html`<section class="block"><h3>Infos du coach</h3><p class="note">${ev.note}</p></section>` : ''}
        ${partnersOn && match.is_home && ctx.adminOf.some(c => c.id === team.club_id) ? html`<div id="ev-guests"></div>` : ''}
        ${partnersOn ? html`<div id="ev-presented"></div>` : ''}
        <div id="ev-support"></div>
        ${editButtons}
      </div>
      <div class="screen mp-pane" data-pane="direct" ${pane('direct')}><div id="ev-lien"></div><div id="ev-stats-live" class="screen"></div></div>
      <div class="screen mp-pane" data-pane="apres" ${pane('apres')}>
        <div id="ev-rs" class="screen"></div>
        ${res ? html`<div id="ev-share"></div>` : ''}
        <div id="ev-stats-after"></div>
        <div id="ev-docs"></div>
        <div id="ev-photos"></div>
        <div id="ev-support-after"></div>
        ${scoreForm}
      </div>
    </section>`);
  else
  mount(root, html`
    <section class="screen">
      <a class="back" href="#/planning">‹ Planning</a>
      <header class="ev-head ev-${ev.kind}">
        <div class="ev-tags">
          <span class="kind kind-${ev.kind}">${match ? COMPETITION_LABEL[match.competition] : KIND_LABEL[ev.kind]}</span>
          ${match && match.is_home !== null ? html`<span class="kind kind-other">${match.is_home ? 'Domicile' : 'Extérieur'}</span>` : ''}
          ${team ? html`<span class="ev-team">${squadName(ctx, match?.squad_id) ? `${squadName(ctx, match?.squad_id)} · ${team.name}` : team.name}</span>` : html`<span class="kind kind-renfort">Renfort</span>`}
        </div>
        <h2 class="${match ? 'ev-vs' : ''}">${match ? html`${oppCrest(match, '', oppLogo)}<span><span class="muted vs">${match.is_home === false ? 'à' : 'contre'}</span> ${match.opponent}</span>` : ev.title}</h2>
        <p class="ev-when">${longDate(date)} · ${timeRange(occ.start, occ.end)}</p>
        ${ev.location || ev.off_plan ? html`<p class="ev-where">${ev.off_plan && staff ? html`<span class="chip chip-mute">Hors planning des salles</span> ` : ''}${ev.location ?? ''}</p>` : ''}
        ${match && res ? html`
          <div class="score score-${res}">
            <span class="score-o">${res === 'win' ? 'Victoire' : res === 'loss' ? 'Défaite' : 'Match nul'}</span>
            <span class="score-n">${scoreLine(match)}</span>
          </div>` : ''}
        ${match?.competition_name ? html`<p class="muted small">${match.competition_name}${match.ffbb_match_id && !isMinorAccount(ctx) ? ' · données FFBB' : ''}</p>` : ''}
      </header>
      ${staff && ev.kind !== 'match' ? seanceSeg(eventId, date, 'seance') : ''}
      ${staff && match ? html`<div id="ev-alert"></div>` : ''}
      ${match?.is_home === false && team ? html`<div id="ev-trip"></div>` : ''}
      ${match && partnersOn ? html`<div id="ev-presented"></div>` : ''}
      ${staff && dayOthers.length ? html`<div id="ev-day" class="screen"></div>` : ''}
      ${match && team ? html`<div id="ev-stats"></div><div id="ev-match" class="screen"></div><div id="ev-docs"></div><div id="ev-photos"></div>` : ''}
      ${staff && ev.kind === 'match' && !match?.ffbb_match_id ? html`
        <section class="block">
          <h3>${res ? 'Score' : 'Saisir le score'}</h3>
          <form class="score-form" id="f-score" novalidate>
            <label><span class="muted small">${squadName(ctx, match?.squad_id) ?? team?.name ?? 'Nous'}</span><input name="us" type="number" inputmode="numeric" min="0" max="300" value="${match?.score_us ?? ''}"></label>
            <span class="score-sep">–</span>
            <label><span class="muted small">${match?.opponent ?? ev.title}</span><input name="them" type="number" inputmode="numeric" min="0" max="300" value="${match?.score_them ?? ''}"></label>
            <button class="btn btn-primary btn-sm" type="submit">Enregistrer</button>
          </form>
        </section>` : ''}
      ${mine.length ? html`<section class="block"><h3>${presenceTitle}</h3>${myBlock}</section>` : ''}
      <div id="ev-rs" class="screen"></div>
      ${ev.note ? html`<section class="block"><h3>Infos du coach</h3><p class="note">${ev.note}</p></section>` : ''}
      ${ev.kind !== 'match' ? html`<div id="plan-block"></div>` : ''}
      ${sheet}
    </section>`);

  const reload = () => renderEvent(root, ctx, eventId, date);
  // D-143 : « Mon ressenti » (moi, mes enfants sans compte) ; pour le staff, les ressentis de l'équipe.
  const rsSlot = $(root, '#ev-rs');
  if (rsSlot && (ev.kind === 'match' || ev.kind === 'training')) void renderFeelingEventBlock(rsSlot, ctx, eventId, date, ev.team_id, staff).catch(() => undefined);
  const pb = $(root, '#plan-block');
  if (pb && staff) void renderPlanSummary(pb, ev.id, date, ev.title).catch(() => undefined);
  else if (pb) void renderPlanBlock(pb, ev, date, occ.start, occ.end, team, staff).catch(() => undefined);
  const presented = $(root, '#ev-presented');
  if (match && team && presented) void renderPresentedBy(presented, ctx, team.club_id, eventId, match.presented_by ?? null, match.is_home);
  // Le soir du match (D-125) : le merci au micro pour la table de marque, la photo avec la pancarte (bureau, staff ; jamais un mineur).
  // D-147 : les partenaires invités à ce match, leur réponse, « Relancer » (le bureau).
  const guests = $(root, '#ev-guests');
  if (guests) void renderMatchGuests(guests, ctx, eventId).catch(() => undefined);
  const lienSlot = $(root, '#ev-lien');
  if (lienSlot && match?.is_home) void renderMatchLien(lienSlot, ctx, eventId).catch(() => undefined);
  // Supporters et mots d'encouragement (D-066). Le staff et les joueuses convoquées et présentes
  // jouent le match ; une joueuse pas retenue, absente ou blessée peut dire « Je viens voir ».
  const meIds = new Set(ctx.people.filter(p => p.user_id === ctx.uid).map(p => p.id));
  const playing = staff || (hero?.retained !== false && mine.some(c => meIds.has(c.person_id) && att.present.includes(c.person_id)));
  if (match && team) for (const id of ['#ev-support', '#ev-support-after']) {
    const x = $(root, id);
    if (x) void renderMatchSupport(x, ctx, eventId, `${teamLabel} – ${match.opponent}`, team.gender, playing);
  }
  // Jour de double match (D-092, D-103) : la carte enregistre d'abord la répartition affichée ; « À faire »
  // et la composition se dessinent ensuite, sur le même état (un seul compte).
  const dayReady: Promise<unknown> = staff && team && meMatch && dayOthers.length
    ? renderDayCards($(root, '#ev-day')!, ctx, team, meMatch, dayOthers, date).catch(() => undefined) : Promise.resolve();
  const names = new Map(roster.map(r => [r.person_id, nameOf(r)]));
  // Jour de double match (D-092) : une joueuse placée depuis l'autre équipe du groupe a aussi son prénom.
  if (dayOthers.length && team) for (const r of await teamRoster(team.id).catch(() => [])) if (!names.has(r.person_id)) names.set(r.person_id, displayName(r));
  if (match && team) {
    void renderStatsBlock($(root, '#ev-stats-live')!, ctx, eventId, match, staff, names, 'live', occ.start).catch(() => undefined);
    void renderStatsBlock($(root, '#ev-stats-after')!, ctx, eventId, match, staff, names, 'after', occ.start).catch(() => undefined);
    // Onglets : on bascule sans recharger ; le choix tient le temps de la visite.
    for (const b of $$<HTMLButtonElement>(root, '[data-phase]')) b.addEventListener('click', () => {
      const p = b.dataset.phase as Phase;
      tabOf.set(eventId, p);
      $$(root, '[data-phase]').forEach(x => x.setAttribute('aria-selected', String(x === b)));
      $$(root, '[data-pane]').forEach(x => { x.hidden = x.dataset.pane !== p; });
    });
    const go = (target: 'prep' | 'compo' | 'car' | 'sheet') => {
      const t = $(root, target === 'sheet' ? '#ev-sheet' : `#mb-${target}-sec`);
      if (!t) return;
      t.scrollIntoView({ behavior: 'smooth', block: 'start' });
      t.classList.remove('mp-flash'); void t.offsetWidth; t.classList.add('mp-flash');
    };
    const todo = $(root, '#ev-todo');
    if (todo) void dayReady.then(() => renderMatchTodo(todo, {
      eventId, date, away: match.is_home === false, gender: team.gender, roster, responses, att, nameOf: pid => names.get(pid) ?? '—', statman: match.statman_user ?? null, go,
      dayName: date === localDate(new Date()) ? 'le match' : parseLocalDate(date).toLocaleDateString('fr-FR', { weekday: 'long' }),
    })).catch(() => undefined);
    const share = $(root, '#ev-share');
    const top = boxScore(actions).sort((a, b) => b.pts - a.pts)[0];
    if (share && res) void renderResultShare(share, {
      eventId, club: hero!.club, team: teamLabel, opponent: match.opponent, isHome: match.is_home, result: res,
      us: match.score_us, them: match.score_them, scoreText: scoreLine(match), dayLabel: hero!.dayLabel,
      top: top && top.pts > 0 ? { name: names.get(top.person_id) ?? '—', pts: top.pts } : null, opponentLogo: oppLogo,
    }).catch(() => undefined);
  }
  if (match && team) void dayReady.then(() => renderMatchBlocks($(root, '#ev-match')!, ctx, {
    eventId, teamId: team.id, staff, present: roster.map(r => r.person_id).filter(pid => expectedOf(att).includes(pid)), uncertain: att.uncertain, absent: att.absent.map(a => a.personId), nameOf: names, mine: mine.map(c => c.person_id),
    gender: team.gender, squadId: match.squad_id ?? null, home: match.is_home === true,
    numbers: new Map(roster.map(r => [r.person_id, r.jersey_number])), past: occ.start < new Date(),
    day: dayOthers[0] ? { otherId: dayOthers[0].event_id, label: `${ctx.squads.filter(q => q.id === match.squad_id || q.id === dayOthers[0]!.squad_id)
      .sort((a, b) => a.position - b.position).map(q => q.name).join(' et ')} jouent le même jour` } : null,
  })).catch(() => undefined);
  if (match?.is_home === false && team) void renderTrip($(root, '#ev-trip')!, ctx, eventId, team.club_id, staff, !!match.ffbb_match_id, reload).catch(() => undefined);
  // D-117 : les alertes FFBB (changement à valider, écart de score) ne se montrent qu'à ceux qu'elles concernent (la même
  // règle que la cloche : le staff de l'équipe ; le bureau pour un match à domicile, ou une équipe sans staff), avec le
  // match nommé en entier. Sans réponse de la base, on montre (le droit de trancher reste vérifié par la base).
  const forMe = staff && match ? alertMine(eventId).catch(() => true) : Promise.resolve(false);
  const label = match && team ? matchLabel({ group: team.name, squad: squadName(ctx, match.squad_id), opponent: match.opponent, isHome: match.is_home, start: occ.start }) : '';
  if (staff && match && team) void forMe.then(mineAlert => renderMatchAlert($(root, '#ev-alert')!, eventId, reload, {
    admin: ctx.adminOf.some(c => c.id === team.club_id), future: occ.start > new Date() && !ev.cancelled_at, start: occ.start, location: ev.location,
    teamId: team.id, opponent: match.opponent, isHome: match.is_home, gender: team.gender, pre: { ctx, clubId: team.club_id, team: teamLabel },
    alertMine: mineAlert, label,
  })).catch(() => undefined);
  // D-113 : « Récupérer le score », pour tous les membres de l'équipe, une fois le match commencé.
  // Le staff devant un 0-0 FFBB a les deux gestes dans le bandeau (« Saisir le score », « Récupérer le score »).
  const fetchSlot = $(root, '#ev-fetch'), scoreSlot = $(root, '#ev-score');
  const zeroInBanner = !!scoreSlot && match?.score_alert === 'zero';
  const linked = match && canFetchScore(match, occ.start)
    ? ffbbLinksOf([ev.team_id]).then(ls => ls.some(l => l.engagement_id === match.ffbb_engagement_id)).catch(() => false) : Promise.resolve(false);
  if (fetchSlot && match && !zeroInBanner) void linked.then(ok => renderFetchScore(fetchSlot, eventId, match, occ.start, ok, () => void reload()));
  // D-113 : le score FFBB diffère du score saisi, ou la FFBB a publié 0-0 : le staff tranche.
  const enterScore = () => {
    $<HTMLButtonElement>(root, '[data-phase="apres"]')?.click();
    const f = $(root, '#ev-score-form');
    f?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    $<HTMLInputElement>(root, '#f-score input[name="us"]')?.focus({ preventScroll: true });
  };
  if (scoreSlot && match) void Promise.all([linked, forMe]).then(([ok, mineAlert]) => (mineAlert
    ? renderScoreAlert(scoreSlot, eventId, match, () => void reload(), enterScore, ok, label)
    // Pas pour moi (le bureau, à l'extérieur) : un 0-0 garde « Récupérer le score », comme pour toute l'équipe.
    : zeroInBanner ? renderFetchScore(scoreSlot, eventId, match, occ.start, ok, () => void reload()) : mount(scoreSlot, html``)));
  if (match && team) void renderMatchDocs($(root, '#ev-docs')!, eventId, staff).catch(() => undefined);
  // D-119 : les bénévoles du match à domicile, en lecture pour le staff (le bureau règle dans Bénévolat).
  const vol = $(root, '#ev-vol');
  if (vol && team) void renderMatchVolunteers(vol, ctx, team.club_id, eventId, date, ctx.adminOf.some(c => c.id === team.club_id)).catch(() => undefined);
  if (match && team) void renderMatchPhotos($(root, '#ev-photos')!, ctx, eventId, staff, res === 'win',
    { teamId: team.id, present: roster.map(r => r.person_id).filter(pid => expectedOf(att).includes(pid)) }).catch(() => undefined);
  const myDay = $(root, '#ev-my-day');
  if (myDay) void renderMyDay(myDay, ctx, eventId, gender, meMatch ? [meMatch, ...dayOthers] : []).catch(() => undefined);
  const fs = $<HTMLFormElement>(root, '#f-score');
  fs?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const d = new FormData(fs);
    const us = String(d.get('us') ?? ''), them = String(d.get('them') ?? '');
    if ((us === '') !== (them === '')) { toast('Indique les deux scores.', 'ko'); return; }
    try {
      await saveMatch({
        event_id: eventId, opponent: match?.opponent ?? ev.title, is_home: match?.is_home ?? null,
        competition: match?.competition ?? 'championnat', score_us: us === '' ? null : Number(us), score_them: them === '' ? null : Number(them),
      });
      toast('Score enregistré'); await reload();
    } catch { toast('Enregistrement impossible.', 'ko'); }
  });
  const save = async (pid: string, status: 'present' | 'absent' | 'uncertain', reason: string | null) => {
    try { await setResponse(eventId, date, pid, status, reason); await reload(); }
    catch { toast('Enregistrement impossible. Réessaie.', 'ko'); }
  };

  const lateTarget = (pid: string) => {
    const who = ctx.people.find(p => p.id === pid);
    return { eventId, date, personId: pid, name: who ? displayName(who) : '', isMe: who?.user_id === ctx.uid, gender: genderOf(ctx, pid) || gender,
      minor: isMinorPerson(who), kind: ev.kind, start: occ.start };
  };
  for (const block of $$(root, '.reply')) {
    const pid = block.dataset.pid!;
    for (const b of $$<HTMLButtonElement>(block, '[data-set]')) {
      // Le choix déjà affiché n'est réenregistré que s'il venait d'office (« Je suis attendue » pendant une règle : l'exception).
      b.addEventListener('click', async () => {
        if (b.getAttribute('aria-pressed') === 'true' && responses.some(r => r.personId === pid)) return;
        // D-148 : se dire absente après la limite ouvre la feuille du désistement tardif (jamais bloqué).
        if (b.dataset.set === 'absent' && (ev.kind === 'training' || ev.kind === 'match')) {
          const dl = await absenceDeadline(eventId, date, pid);
          if (dl?.late) { openLateSheet(lateTarget(pid), dl, () => reload()); return; }
        }
        void save(pid, b.dataset.set as 'present' | 'absent' | 'uncertain', null);
      });
    }
    const input = block.querySelector<HTMLInputElement>('[data-reason]');
    input?.addEventListener('change', () => save(pid, input.dataset.st as 'absent' | 'uncertain', input.value));
  }
  for (const b of $$<HTMLButtonElement>(root, '[data-pres]')) {
    b.addEventListener('click', () => {
      const pid = b.dataset.pres!, v = b.dataset.v as 'present' | 'absent' | 'uncertain';
      if (b.getAttribute('aria-pressed') === 'true') return;   // déjà le choix en cours
      b.disabled = true;
      void save(pid, v, null);
    });
  }
  // D-148 : « Ne pas compter », « Ne pas lui écrire » ; la relance « Je serai absente » arrivée après la limite.
  if (staff) wireBoard(root, () => reload());
  if (lateAsk && confirmFor) openLateSheet(lateTarget(confirmFor), lateAsk, () => reload());
  // L'exception pour cette séance (D-114, maquette i05b) : la règle reste ; la joueuse peut être prévenue.
  for (const b of $$<HTMLButtonElement>(root, '[data-unrule]')) {
    b.addEventListener('click', () => {
      const pid = b.dataset.unrule!, who = nm(pid), v = verdictOf(pid, actx);
      const rule = v?.source === 'rule' ? avail.rules.find(r => r.id === v.ref) : null;
      const period = v?.source === 'period' ? avail.periodRows.find(u => u.id === v.ref) : null;
      const dayLong = parseLocalDate(date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
      const why = rule ? html`par sa règle : <strong>${ruleSentence(rule).replace(/[.]$/, '').replace(/^./, x => x.toLowerCase())}</strong>`
        : period ? html`: <strong>indisponible ${period.starts_on === period.ends_on ? `le ${shortDate(period.starts_on)}` : `du ${periodRange(period.starts_on, period.ends_on)}`}</strong>` : '';
      openSheet(agree(gender, 'Le remettre présent ?', 'La remettre présente ?'), html`
        <p>${who} est ${agree(gender, 'absent', 'absente')} d’office ${why}.</p>
        <p class="ix-preview">${P} pour <b>${ev.kind === 'match' ? `le match du ${dayLong}` : `le ${dayLong}`} seulement</b>. ${rule ? 'La règle reste pour les autres semaines.' : 'Le reste de son indisponibilité ne change pas.'}</p>
        <label class="check"><input type="checkbox" name="notify" checked><span>Prévenir ${who}</span></label>
        <button type="button" class="btn btn-primary" id="ix-exc">${agree(gender, 'Le remettre présent', 'La remettre présente')} ${weekday}</button>`, (sh, close) => {
        $(sh, '#ix-exc')!.addEventListener('click', async () => {
          try {
            await occurrenceException(eventId, date, pid, 'present', $<HTMLInputElement>(sh, '[name=notify]')!.checked);
            close(); toast(`${who} ${agree(gender, 'remis présent', 'remise présente')} pour ${weekday}`); await reload();
          } catch { toast('Impossible pour le moment.', 'ko'); }
        });
      });
    });
  }

  // --- Staff : renforts et annulation -----------------------------------------
  for (const b of $$<HTMLButtonElement>(root, '[data-unrenfort]')) {
    b.addEventListener('click', async () => {
      try { await removeRenfort(eventId, date, b.dataset.unrenfort!); await reload(); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    });
  }
  $(root, '#b-renfort')?.addEventListener('click', async () => {
    if (!team) return;
    const already = new Set(roster.map(r => r.person_id));
    const [all, games, trip] = await Promise.all([renfortCandidates(eventId, date), renfortWeekend(eventId, date).catch(() => [] as RenfortGame[]),
      ev.kind === 'match' ? matchTrip(eventId).catch(() => null) : Promise.resolve(null)]);
    const cands = all.filter(p => !already.has(p.person_id));
    const cat = team.category;
    // Ses matchs du week-end (Idries : « le détail des matchs qu'elle joue ce week-end-là, extérieur, temps de trajet…
    // comme pour la répartition entre les 2 équipes ») ; le même jour qu'un match : doubler est-il possible ?
    const target: GameLite | null = ev.kind === 'match' ? {
      start: new Date(ev.starts_at), home: match?.is_home ?? null, opponent: match?.opponent ?? '',
      at: trip?.to ?? (match?.is_home ? trip?.from ?? null : null), tripMin: trip?.trip?.min ?? null,
    } : null;
    const gamesOf = new Map<string, RenfortGame[]>();
    for (const g of games) gamesOf.set(g.person_id, [...(gamesOf.get(g.person_id) ?? []), g]);
    const weekend = (p: RenfortCandidate) => (gamesOf.get(p.person_id) ?? []).map(g => {
      const start = new Date(g.starts_at), sameDay = localDate(start) === date;
      const at = g.venue_lat != null && g.venue_lng != null ? { lat: g.venue_lat, lng: g.venue_lng } : null;
      // Route pas encore calculée : estimée depuis notre gymnase, si la salle est localisée.
      const est = g.trip_min == null && g.is_home === false && !!at && !!trip?.from;
      const game: GameLite = { start, home: g.is_home, opponent: g.opponent, at,
        tripMin: g.trip_min ?? (est ? roadEstimate(crowKm(trip!.from!, at!)) : null) };
      const v = sameDay && target ? renfortDouble(target, game) : null;
      const day = sameDay ? 'Même jour' : start.toLocaleDateString('fr-FR', { weekday: 'short' }).replace(/^./, c => c.toUpperCase());
      return html`<span class="rf-game"><b>${day} ${time(start)}</b> · ${g.squad ?? g.team} – ${g.opponent} · ${gameWhere(game, est)}</span>
        ${v ? html`<span class="${v.ok ? 'rf-ok' : 'pick-warn'}">${v.ok ? '✓ ' : ''}${v.lead} : ${v.detail}</span>` : ''}`;
    });
    // D-133 : seules celles et ceux qui peuvent vraiment jouer ce match ; repliés, les surclassements en cours et les
    // fiches à compléter ; une ligne dit qui n'est pas proposé, et pourquoi.
    const row = (p: RenfortCandidate) => {
      // Surclassement demandé mais pas encore en vigueur : convocable, avec un avertissement (D-030).
      const soon = p.status === 'pending';
      const can = p.status === 'ok' || soon;
      const wk = weekend(p);
      const sameDayGame = (gamesOf.get(p.person_id) ?? []).some(g => localDate(new Date(g.starts_at)) === date);
      const up = p.surclassements.map(x => html`<span class="chip chip-ok">${p.double_up && x === cat ? 'Double surclassement' : agree(gender, 'Surclassé', 'Surclassée')} ${x}</span>`);
      return html`<li><button type="button" ${can ? html`data-pick="${p.person_id}"` : html`disabled aria-disabled="true"`}
        data-q="${`${p.first_name} ${p.nickname ?? ''} ${p.teams.join(' ')}`.toLowerCase()}" class="${can ? '' : 'pick-off'}">
        <span class="pick-main">
          <strong>${displayName(p)}</strong>
          <span class="muted small">${p.teams.join(', ') || 'sans équipe'}</span>
          ${up.length || p.pending.length ? html`<span class="occ-chips">${up}${p.pending.map(x => html`<span class="chip chip-mute">Surclassement ${x} en cours</span>`)}</span>` : ''}
          ${wk.length ? html`<span class="rf-games">${wk}</span>` : ''}
          ${p.conflict && !sameDayGame ? html`<span class="pick-warn">Joue aussi ce jour-là : ${p.conflict}</span>` : ''}
          ${soon ? html`<span class="pick-warn">Surclassement ${cat ?? ''} pas encore en vigueur (dépôt au comité) : ne peut pas jouer en match avant.</span>` : ''}
          ${p.status === 'verify' ? html`<span class="small muted">${verifyText(p.reason)}</span>` : ''}
        </span>
      </button></li>`;
    };
    const ready = cands.filter(p => p.status === 'ok'), soonList = cands.filter(p => p.status === 'pending'), check = cands.filter(p => p.status === 'verify');
    const fold = (title: string, sub: string, list: RenfortCandidate[], open = false) => list.length ? html`
      <details class="rf-sec alt-box" ${open ? 'open' : ''}><summary>${title} · ${list.length} <small>${sub}</small></summary><ul class="pick-list">${list.map(row)}</ul></details>` : '';
    const notOffered = renfortNotOffered(cat, gender);
    openSheet('Convoquer un renfort', html`
      <input class="search" type="search" placeholder="Chercher un prénom, une équipe…" aria-label="Chercher">
      <p class="hint">Pour ${ev.kind === 'match' ? 'ce match' : 'cette date'} seulement : ${longDate(date).replace(/^./, c => c.toLowerCase())}. ${renfortHint(cat, gender)}</p>
      ${ready.length ? html`<ul class="pick-list" data-guide="rf-list">${ready.map(row)}</ul>`
        : html`<p class="muted">Personne d’autre ne peut jouer ce match pour l’instant.</p>`}
      ${fold('Surclassement en cours', 'possible bientôt', soonList)}
      ${fold('À vérifier', verifyLabel(check.map(p => p.reason)), check, !ready.length)}
      ${notOffered ? html`<p class="muted small rf-none">${notOffered}</p>` : ''}`, (sheet, close) => {
      const search = $<HTMLInputElement>(sheet, '.search')!;
      search.addEventListener('input', () => {
        const q = search.value.trim().toLowerCase();
        $$(sheet, '[data-q]').forEach(b => { b.parentElement!.hidden = !!q && !b.dataset.q!.includes(q); });
      });
      for (const b of $$<HTMLButtonElement>(sheet, '[data-pick]')) {
        b.addEventListener('click', async () => {
          try { await inviteRenfort(eventId, date, b.dataset.pick!); close(); toast('Renfort convoqué'); await reload(); }
          catch { toast('Convocation impossible.', 'ko'); }
        });
      }
    });
  });
  $(root, '#b-edit-ev')?.addEventListener('click', () => openEventEdit(ev, () => void reload(), ctx));
  const cancel = $<HTMLButtonElement>(root, '#b-cancel');
  if (cancel) confirmTwice(cancel, `Confirmer l'annulation du ${longDate(date).toLowerCase()}`, async () => {
    try { await cancelOccurrence(ev, date); toast(ev.kind === 'match' ? 'Match annulé' : 'Rendez-vous annulé'); location.hash = '#/'; }
    catch { toast('Annulation impossible.', 'ko'); }
  });
}

/** Staff : le plan de la séance en un coup d'œil, et la page pour le composer (D-043). */
async function renderPlanSummary(el: HTMLElement, eventId: string, date: string, title: string) {
  const plan = await sessionPlan(eventId, date).catch(() => null);
  const n = plan?.items.length ?? 0;
  mount(el, html`
    <section class="block plan-sum">
      <div class="block-head"><h3>Plan de séance</h3>${plan ? html`<span class="chip ${plan.published ? 'chip-ok' : 'chip-warn'}">${plan.published ? 'Envoyé' : 'Brouillon'}</span>` : ''}</div>
      ${n ? html`<p><strong>${n} ${n > 1 ? 'exercices' : 'exercice'} · ${totalMinutes(plan!.items)} min</strong>${plan!.theme ? html`<br><span class="muted">${plan!.theme}</span>` : ''}</p>
        <p class="muted small">${plan!.items.slice(0, 4).map(i => i.title).join(' → ')}${n > 4 ? '…' : ''}</p>` : html`<p class="muted">Pas encore de plan.</p>`}
      <div class="btn-row">
        <a class="btn btn-primary btn-sm" href="#/seance/${eventId}/${date}" data-replace>${n ? 'Composer la séance' : 'Préparer la séance'} →</a>
        ${n ? html`<button type="button" class="btn btn-ghost btn-sm" id="b-sum-live">▶ Lancer</button>` : ''}
      </div>
    </section>`);
  $(el, '#b-sum-live')?.addEventListener('click', () => { if (plan) openLive(plan.items, title); });
}
